/* Legends free-gift promo — pure sync logic, no DOM and no network.
   Loaded before legends-cart-drawer.js, which performs the operations.

   The promo was band-matched hard cases until 2026-08-26; it is now a single Lens Cleaner
   Kit that goes with every frame regardless of band. The file, the asset name and the
   `_free_case` line property all keep their old names on purpose: renaming a theme asset
   means a push-plus-delete against a live store, and the property name is what tells the
   planner that a gift line left over from the hard-case promo is a gift line at all. Read
   "case" as "gift" throughout. */
(function (window) {
  'use strict';

  var GIFT_PROP = '_free_case';
  /* Written onto every gift line this planner adds. The value is never read for meaning —
     staleness is decided on the variant, not on this — so it stays a constant rather than
     the band tag it used to be. It must simply be non-blank: cart-item.liquid gates the
     locked-line rendering on `properties._free_case != blank`. */
  var GIFT_MARK = '1';

  /* The variant a cart line holds. /cart.js gives both `variant_id` and `id` (they are
     the same value); neither is guaranteed on a hand-built line, hence the null. */
  function lineVariantId(item) {
    if (item.variant_id != null) return item.variant_id;
    if (item.id != null) return item.id;
    return null;
  }

  /* The selling plan a cart line was bought on, or null. /cart.js nests it under
     selling_plan_allocation; a hand-built line may not carry it at all. */
  function linePlanId(item) {
    var alloc = item.selling_plan_allocation;
    if (!alloc || !alloc.selling_plan) return null;
    var id = alloc.selling_plan.id;
    return id == null ? null : id;
  }

  /* The product a cart line belongs to. Frames are recognised by this rather than by
     handle because Shopify TRANSLATES handles per locale: on /de/ the same frame is
     `iron-maiden-50-jahre-sonnenbrille`, on /es/ `...-lentes-de-sol`. The Liquid-rendered
     map takes the locale of the page that rendered it while /cart.js returns the default
     locale's handles, so the two silently disagree and no frame ever matches. Product ids
     do not translate. /cart.js always carries product_id; a hand-built line may not. */
  function lineProductId(item) {
    if (item.product_id != null) return item.product_id;
    return null;
  }

  /* True when a line carrying the gift property does NOT hold the current gift variant.
     Two product swaps have now made old gift lines dangerous. Before 2026-08-06 the
     "gift" was the £15 purchasable case; since 2026-08-26 it is the Lens Cleaner Kit
     rather than a hard case. In both cases a cart built before the swap still holds the
     old variant under the gift property, and the automatic 100%-off discount is scoped to
     the gift collection, so whether it reaches that line is a matter of luck. Treat any
     such line as stale: replace it, never count it as the gift.

     Two cases deliberately return false rather than "stale": no configured gift (nothing
     to compare against and nothing to replace it with) and a line whose variant cannot be
     read. Neither is evidence of staleness, and deleting blind on a missing value would
     destroy a good line. */
  function isStaleGift(item, gift) {
    if (!gift || !gift.variantId) return false;
    var vid = lineVariantId(item);
    if (vid == null) return false;
    return String(vid) !== String(gift.variantId);
  }

  /* Returns the operations needed to make the cart's gift lines match its frames.
     Pure: performs no I/O and mutates nothing. Returns [] when already correct.

     One gift per frame, one gift LINE however many frames — the gift is the same product
     for every band now, so a two-band cart gets a single line of quantity two rather than
     two identical lines the customer would read as a duplicate. */
  function computeCaseSyncOps(cart, config) {
    if (!cart || !cart.items || !config || !config.enabled) return [];
    var frameIds = config.frameProductIds || {};
    var gift = config.gift || null;

    var desired = 0;      // how many gifts the cart should hold
    var current = null;   // the existing, correct gift line
    var stale = [];       // gift lines holding a variant that is no longer the gift
    var planId = null;    // the plan the frames were bought on
    var i, it;

    for (i = 0; i < cart.items.length; i++) {
      it = cart.items[i];
      if (!it) continue;

      var props = it.properties || {};
      if (props[GIFT_PROP]) {
        // An existing gift line. Last one wins if somehow duplicated. A line holding the
        // wrong variant is not the gift at all — it is removed below and the cart is then
        // treated as having no gift, so the correct one gets added.
        if (isStaleGift(it, gift)) {
          stale.push(it);
        } else {
          // A second correct gift line should not exist, but if one does the later line
          // wins and the earlier is queued for removal — keeping both doubles the giveaway.
          if (current) stale.push(current);
          current = it;
        }
        continue;
      }

      // Only products in the frames collection count, so the gift itself — and any case
      // or kit the customer actually paid for — can never count itself as a frame.
      var pid = lineProductId(it);
      if (pid != null && frameIds[pid]) {
        desired += it.quantity;
        // Mirror the frame's pre-order plan onto the gift so both land in the same
        // delivery group. Without it Shopify sees "ships now" against the frame's dated
        // group and bills postage twice (UK £7.98 vs £3.99, measured at real checkout
        // 2026-08-11). First frame that carries a plan wins; a plan-less frame must not
        // veto a later one that has one.
        if (planId == null) {
          var p = linePlanId(it);
          if (p != null) planId = p;
        }
      }
    }

    var ops = [];
    var j;

    // Stale gift lines go first. Removing before adding means the cart never briefly holds
    // two lines both claiming to be the gift, and if the replacement add then fails the
    // customer is left with no gift rather than charged for one the UI called free. A stale
    // line in a cart with no frames left is simply removed: nothing asks for a replacement.
    for (j = 0; j < stale.length; j++) {
      ops.push({ type: 'change', key: stale[j].key, quantity: 0 });
    }

    if (desired > 0) {
      if (current) {
        if (current.quantity !== desired) {
          ops.push({ type: 'change', key: current.key, quantity: desired });
        }
      } else if (gift && gift.variantId) {
        var addOp = { type: 'add', variantId: gift.variantId, quantity: desired };
        // Only set when the frames actually carry a plan, so a non-pre-order cart keeps
        // producing a plain add.
        if (planId != null) addOp.sellingPlanId = planId;
        ops.push(addOp);
      }
      // No gift configured: leave the cart alone rather than guess.
    } else if (current) {
      // The gift line's frames have all gone.
      ops.push({ type: 'change', key: current.key, quantity: 0 });
    }

    return ops;
  }

  window.LegendsFreeCase = {
    GIFT_PROP: GIFT_PROP,
    GIFT_MARK: GIFT_MARK,
    computeCaseSyncOps: computeCaseSyncOps
  };
})(typeof window !== 'undefined' ? window : this);
